import { ChatGroupId } from '@/chat/models/ChatGroup';
import { parseEntities, parseEntity } from '@/shared/converters/commonConverters';
import { DateTimeFormat, formatMoment, parseMoment } from '@/shared/converters/dateConverters';
import { parseMobilitySolution } from '@/shared/converters/mobolitySolutionConverters';
import { parseTags } from '@/shared/converters/tagConverters';
import Tag from '@/shared/models/Tag';
import { UserId } from '@/shared/models/User';
import { QrCode } from '@/shared/models/common';
import { isPresent, present } from '@/shared/utils/typescript';
import { parseDossierStacks } from '@/tasks/converters/dossierStackConverters';
import { formatLocation } from '@/vehicle/converters/locationConverters';
import Location from '@/vehicle/models/Location';
import Vehicle from '@/vehicle/models/Vehicle';
import BarcodeType from '../constants/BarcodeType';
import CleaningStatus from '../constants/CleaningStatus';
import DossierOverviewItem from '../constants/DossierOverviewItem';
import { CheckInData, CheckInExtraJob } from '../models/CheckInData';
import Dossier from '../models/Dossier';
import DossierOverviewRequest from '../models/DossierOverviewRequest';
import DossierSummary from '../models/DossierSummary';
import DossierWorkshopPlanning from '../models/DossierWorkshopPlanning';
import { PickedUpData } from '../models/PickedUpData';
import { ReadyForPickupClientData } from '../models/ReadyForPickupData';
import { parseTransportContainer } from './transportConverters';
import { CheckInFormData } from '@/dossier/models/CheckInContext';
import SlaNoteType from '../models/SlaNoteType';

export const ABSTRACT_DOSSIER_SCHEMA = {
  id: 'id',
  appointmentOn: (data: any) => parseMoment(data.appointment, DateTimeFormat.LEGACY),
  assignedDescription: 'assignedTo.description',
  assignedGroupId: parseAssignedGroupId,
  assignedUserId: parseAssignedUserId,
  checkedInAt: (data: any) => parseMoment(data.checkedInAt),
  exteriorCleaningStatus: 'cleaningStatus',
  externalReference: 'externalReference',
  interiorCleaningStatus: 'interiorCleaningStatus',
  isInvoiced: 'isInvoiced',
  keyBoxStatus: 'keyboxStatus',
  outletId: 'outletId',
  partsOrderStatus: 'partsOrderStatus',
  partsPickingStatus: 'partsPickingStatus',
  paymentStatus: 'paymentRequestStatus',
  preferredPickUpOn: (data: any) => parseMoment(data.preferredPickUp, DateTimeFormat.LEGACY),
  qualityControlStatus: 'qualityControlStatus',
  tags: (data: any) => parseTags(data.tags),
  vehicleStatus: 'objectStatus',
  waitsOnPremise: 'waitsOnPremise',
  warrantyStatus: 'warrantyStatus',
  workOnHoldReason: (data: any) => parseWorkOnHoldReason(data.workOnHoldReason),
  workPreparationStatus: 'workPreparationStatus',
  workStatus: 'workStatus',
  workshopTeamId: 'teamId',
};

const SCHEMA = {
  ...ABSTRACT_DOSSIER_SCHEMA,
  dmsBarcodePrefix: 'barcodePrefix',
  dmsBarcodeType: (data: any) => parseBarcodeType(data.barcodeType),
  dmsConnectionName: 'dmsConnectionName',
  exteriorCleaningMethod: 'cleaningMethod',
  externalChatCounter: 'chatCounter',
  externalChatId: 'chatId',
  fluidLevelsChecked: 'isFluidsLevelChecked',
  followUpOn: (data: any) => parseMoment(data.newAppointmentUTC, DateTimeFormat.DATE_TIME),
  hasChecklists: 'content.hasChecklists',
  hasConsumptionDataApproval: 'hasConsumptionDataApproval',
  hasDirectReceptionApproval: 'hasDirectReceptionApproval',
  hasFluidReplenishmentApproval: 'hasFluidsReplenishmentApproval',
  hasFluids: 'content.hasFluids',
  hasKeyboxAvailable: 'hasKeyboxAvailable',
  hasSurveys: 'content.hasCSS',
  hasTestDrives: 'content.hasTestDrives',
  hasTires: 'content.hasTires',
  internalChatCounter: 'internalChatCounter',
  internalChatId: 'internalChatId',
  isCarwashProhibited: 'isCarwashProhibited',
  isLeasing: 'isLeasing',
  isVisible: (data: any) => typeof data.signableOn === 'string',
  keyBoxStatus: 'keyboxStatusId',
  keyCode: (data: any) => parseKeyCode(data.pdcPrintCode),
  mechanicPausedStatus: 'mechanicPausedStatus',
  minutesPerTimeUnit: 'minutesPerTimeUnit',
  mobilitySolution: (data: any) => parseMobilitySolution(data.mobilitySolution),
  note: 'note',
  partsPickingLocation: 'partsPickingLocation',
  qualityControlStatus: 'qualityControlStatus',
  slaNotes: (data: any) => parseDossierSlaNotes(data.slaNotes),
  slaNotesTypes: (data: any) => parseSlaNotesTypes(data.slaNotesTypes),
  stacks: (data: any) => parseDossierStacks(data.stacks),
  transport: (data: any) => parseTransportContainer(data.pickUpDelivery),
  type: 'documentType',
  vat: 'vat',
  workshopPlannings: (data: any) => parseWorkshopPlannings(data.workshopPlannings),
  workshopTeamId: 'workshopPlanningTeam.id',
};

const SUMMARY_SCHEMA = {
  ...ABSTRACT_DOSSIER_SCHEMA,
  contacts: 'contacts',
  externalReference: 'dmsReference',
  isPreCheckedIn: 'isPreCheckedIn',
  mechanicIds: 'workedOnByIds',
  order: 'order',
  partsPickingStatus: 'pickingStatus',
  vehicle: (data: any) => {
    const vehicle = new Vehicle();
    vehicle.registrationNumber = data.registrationNumber;
    vehicle.modelDescription = data.modelDescription;
    return vehicle;
  },
};

const PLANNING_SCHEMA = {
  end: (data: any) => parseMoment(data.scheduledTill, DateTimeFormat.LEGACY),
  eventId: 'id',
  mechanicId: 'resource.id',
  paused: 'isMechanicPaused',
  start: (data: any) => parseMoment(data.scheduledFrom, DateTimeFormat.LEGACY),
};

const WORK_ON_HOLD_REASON_SCHEMA = {
  colorHex: 'colorHex',
  description: 'description',
  id: 'id',
  isPause: () => true,
};

const DOSSIER_SLA_NOTE_SCHEMA = {
  subject: 'subject',
  content: 'content',
};

const SLA_NOTE_TYPE_SCHEMA = {
  id: 'id',
  description: 'description',
};

export class DossierSlaNote {
  subject!: string;
  content = '';
}

export function parseSlaNoteType(data: any) {
  return parseEntity(data, SLA_NOTE_TYPE_SCHEMA, SlaNoteType);
}

export function parseSlaNotesTypes(data: any) {
  return parseEntities(data, parseSlaNoteType);
}

export function parseDossierSlaNote(data: any) {
  return parseEntity(data, DOSSIER_SLA_NOTE_SCHEMA, DossierSlaNote);
}

export function parseDossierSlaNotes(data: any) {
  return parseEntities(data, parseDossierSlaNote);
}

export function parseDossier(data: any): Dossier | null {
  return parseEntity(data, SCHEMA, Dossier);
}

export function parseDossierSummary(data: any): DossierSummary | null {
  return parseEntity(data, SUMMARY_SCHEMA, DossierSummary);
}

export function parseDossierSummaries(data: any): Array<DossierSummary> {
  return parseEntities(data, parseDossierSummary);
}

function parseWorkshopPlannings(data: any): Array<DossierWorkshopPlanning> {
  return parseEntities(data, PLANNING_SCHEMA, DossierWorkshopPlanning);
}

function parseWorkOnHoldReason(data: any): Tag | null {
  return parseEntity(data, WORK_ON_HOLD_REASON_SCHEMA, Tag);
}

export function parseBarcodeType(data: any): BarcodeType | null {
  switch (data) {
    case 'CODE-39':
      return BarcodeType.CODE_39;
    case 'CODE-128':
      return BarcodeType.CODE_128;
    case 'UPC-A':
      return BarcodeType.UPC;
    case 'QR':
      return BarcodeType.QR;
    default:
      return null;
  }
}

export function parseKeyCode(data: any): QrCode | null {
  if (!data) return null;
  return `${data}`.replaceAll('|~|', '\n');
}

export function parseAssignedUserId(data: any): UserId | null {
  const { assignedTo } = data;
  if (assignedTo && !assignedTo.isGroup) return assignedTo.id;
  return null;
}

export function parseAssignedGroupId(data: any): ChatGroupId | null {
  const { assignedTo } = data;
  if (assignedTo?.isGroup) return assignedTo.id;
  return null;
}

export function formatCheckInData(data: CheckInData, dossier: Dossier): any {
  return {
    objectInInfo: {
      // todo remove cleaningStatus and cleaningMethod when backend removed it
      cleaningMethod: dossier.exteriorCleaningMethod,
      cleaningStatus: [null, CleaningStatus.PROHIBITED].includes(dossier.exteriorCleaningStatus) ? null : CleaningStatus.TO_DO,
      hasConsumptionDataApproval: data.hasConsumptionDataApproval,
      hasDirectReceptionApproval: true,
      hasFluidsReplenishmentApproval: data.contact ? data.extraJobs.includes(CheckInExtraJob.FLUIDS) : false,
      isVerifiedMileage: data.isVerifiedMileage,
      keyLabel: null,
      mileage: data.mileage,
      objectLocation: data.position ? formatLocation(Location.create(data.locationDescription, data.position)) : data.locationDescription,
      preferredPickUp: formatMoment(dossier.preferredPickUpOn, DateTimeFormat.LEGACY),
      waitsOnPremise: dossier.waitsOnPremise,
      setDealerSignature: data.setDealerSignature,
    },
  };
}

export function formatCheckInData2(data: CheckInFormData, dossier: Dossier): any {
  return {
    objectInInfo: {
      hasConsumptionDataApproval: data.hasConsumptionDataApproval,
      hasDirectReceptionApproval: false,
      hasFluidsReplenishmentApproval: data.refillLiquids,
      isVerifiedMileage: data.isVerifiedMileage,
      keyLabel: data.keyLabel || null,
      mileage: data.mileage,
      objectLocation: (data.position ? formatLocation(Location.create(data.locationDescription, data.position)) : data.locationDescription) || null,
      preferredPickUp: formatMoment(dossier.preferredPickUpOn, DateTimeFormat.LEGACY),
      waitsOnPremise: dossier.waitsOnPremise,
      setDealerSignature: data.confirmedByUser,
      ASDisclaimerId: isPresent(data.contact?.id) ? (data.signedDocuments[0]?.id ?? null) : null,
      ASDisclaimerById: data.contact?.id ?? null,
      communicationPreference: data.communicationPreference ?? null,
    },
  };
}

export function formatDossierTag(tag: Tag): object {
  return {
    id: tag.id,
    tagPauseReasonDescription: null,
  };
}

export function formatDossierTags(tags: Array<Tag>): Array<object> {
  return tags.map(formatDossierTag);
}

export function formatReadyForPickupData(data: ReadyForPickupClientData): any {
  return {
    objectReadyForOutInfo: {
      isPaymentRequired: false,
      isWarranty: data.warranty,
      jobsWithFollowUpApppointment: data.jobs.map(job => job.id), // typo in key is on purpose
      nextInviteDate: null,
      paymentInformation: data.payment ? {
        amount: data.payment.amount,
        moboMediaFileId: data.payment.attachment ?? null,
        pdfFile: null,
        reference: data.payment.reference,
      } : null,
      targets: data.targets.map(t => ({
        id: present(t.contact.id),
        identifier: t.target,
      })),
    },
  };
}

export function formatPickedUpData(data: PickedUpData): any {
  return {
    objectOutInfo: {
      dossierContactId: data.contact?.id ?? null,
      isManaged: data.keepAssigned,
      isWarranty: data.warranty,
      jobsWithFollowUpApppointment: [], // typo in key is on purpose
      signature: data.signature,
    },
  };
}

export function formatDossierOverviewRequest(request: DossierOverviewRequest): any {
  return {
    comment: request.comment,
    hasAttachments: request.visibleItems.includes(DossierOverviewItem.ATTACHMENT),
    hasCustomerSignatures: request.visibleItems.includes(DossierOverviewItem.CUSTOMER_SIGNATURE),
    hasDealerSignature: request.visibleItems.includes(DossierOverviewItem.DEALER_SIGNATURE),
    hasDiagnosticSteps: request.visibleItems.includes(DossierOverviewItem.DIAGNOSTIC),
    hasJobs: request.visibleItems.includes(DossierOverviewItem.JOB),
    hasMedia: request.visibleItems.includes(DossierOverviewItem.MEDIA),
    hasOnlyWarrantyJobs: request.visibleItems.includes(DossierOverviewItem.WARRANTY),
    hasQualityControls: request.visibleItems.includes(DossierOverviewItem.QUALITY_CONTROL),
    hasTestDrives: request.visibleItems.includes(DossierOverviewItem.TEST_DRIVE),
    hasTimeVisible: request.visibleItems.includes(DossierOverviewItem.TIME),
    language: request.language,
    type: request.recipient,
    validTill: formatMoment(request.validTill),
  };
}